import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyArticle() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('back_to_home')}
            </button>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-4">
                {t('article_category_safety')}
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                Is CSSBuy Safe? Complete Safety Guide 2026
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{t('published_date')}: 2026-08-22</span>
                <span>{t('updated_date')}: 2026-08-27</span>
                <span>5 min read</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20security%20shield%20and%20padlock%20on%20soft%20neutral%20linen%20background%20warm%20natural%20light%20editorial%20product%20photography%20clean%20aesthetic%20safety%20protection%20trust%20concept%20premium%20quality&width=1200&height=500&seq=article-hero-safety&orientation=landscape"
                alt="CSSBuy Safety Guide"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Is CSSBuy Legit and Safe?
                </h2>
                <p>
                  CSSBuy has been operating as a purchasing agent for several years, serving customers who want to buy products from Chinese marketplaces. Like any international purchasing platform, CSSBuy comes with both benefits and risks. This guide provides an honest assessment of the safety factors you should consider before using the service.
                </p>
                <p>
                  It is important to understand that while CSSBuy provides legitimate agent services, the overall safety of your experience depends on multiple factors: the sellers you choose, the products you buy, your understanding of the shipping process, and your own precautions. No platform can guarantee 100% safety, but informed buyers can significantly reduce their risks.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Understanding the Risks
                </h2>
                <p>
                  When buying through any agent service, you face several types of risks. The most common include seller reliability (will the seller send the correct item?), product quality (will the item match the description?), shipping issues (will the package arrive safely?), and customs problems (will your package pass through customs without issues?).
                </p>
                <p>
                  CSSBuy acts as an intermediary between you and the sellers. They handle the purchase, inspection, and shipping, but they cannot control the quality or authenticity of the products themselves. Their role is to facilitate the process and provide tools like QC photos, but the responsibility for choosing trustworthy sellers ultimately falls on you as the buyer.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  How to Protect Yourself
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
                  <div className="bg-white p-5 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-3">
                      <i className="ri-camera-lens-line text-xl" />
                    </div>
                    <h3 className="font-medium text-foreground-900 mb-1">Always Use QC Photos</h3>
                    <p className="text-sm text-foreground-600">Request and carefully review quality control photos before approving shipment.</p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-3">
                      <i className="ri-user-search-line text-xl" />
                    </div>
                    <h3 className="font-medium text-foreground-900 mb-1">Research Sellers</h3>
                    <p className="text-sm text-foreground-600">Check seller reviews, ratings, and community feedback before placing orders.</p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 mb-3">
                      <i className="ri-shield-check-line text-xl" />
                    </div>
                    <h3 className="font-medium text-foreground-900 mb-1">Use Secure Payments</h3>
                    <p className="text-sm text-foreground-600">Choose payment methods that offer buyer protection whenever possible.</p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-3">
                      <i className="ri-box-3-line text-xl" />
                    </div>
                    <h3 className="font-medium text-foreground-900 mb-1">Start Small</h3>
                    <p className="text-sm text-foreground-600">Begin with small, low-value orders to test the process before larger purchases.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Common Scams to Avoid
                </h2>
                <p>
                  While most sellers operate honestly, there are some common scams to be aware of. Bait-and-switch scams involve sellers showing photos of high-quality products but sending lower-quality versions. Empty box scams involve sellers sending packages with nothing inside or incorrect items. Fake tracking scams involve sellers providing invalid tracking numbers.
                </p>
                <p>
                  To avoid these scams, always use the QC photo service, never approve shipping until you have verified the photos, and report any suspicious activity to CSSBuy customer service. If a deal seems too good to be true, it probably is. Extremely low prices are often a red flag for scams or poor quality products.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  Customs and Import Risks
                </h2>
                <p>
                  Depending on your country, your packages may be subject to customs inspection and import duties. In Brazil, for example, packages over a certain value are typically taxed, and some items may be restricted or require additional documentation. Understanding your local customs regulations is essential to avoid unexpected fees or package seizures.
                </p>
                <p>
                  CSSBuy provides some guidance on shipping declarations, but you are responsible for understanding your country's import laws. Always use realistic declared values, as extremely low declarations can raise suspicions and lead to more thorough inspections. In some cases, packages with suspicious declarations may be seized entirely.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                  What to Do If Something Goes Wrong
                </h2>
                <p>
                  If you encounter a problem with your order, the first step is to contact CSSBuy customer service. They can help you communicate with the seller, request returns or exchanges, and provide guidance on the next steps. For QC-related issues, you can request additional photos or initiate a return before the item is shipped from the warehouse.
                </p>
                <p>
                  If your package is lost or damaged in transit, contact the shipping carrier with your tracking number. Some shipping methods include insurance, which may cover lost or damaged items. Keep all documentation, photos, and communication records in case you need to file a claim.
                </p>
              </section>

              <section className="bg-background-100 rounded-xl p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">Is CSSBuy a scam?</h3>
                    <p className="text-sm">No, CSSBuy is a legitimate agent service. However, as with any platform, individual sellers may not be trustworthy.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">Can I get a refund?</h3>
                    <p className="text-sm">Refund policies depend on the seller and the stage of the order. QC stage returns are generally easier than post-shipping returns.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">How do I verify a seller?</h3>
                    <p className="text-sm">Check seller ratings, reviews, and community feedback. Avoid sellers with no history or negative reviews.</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">What if QC photos look wrong?</h3>
                    <p className="text-sm">Do not approve the item. Request additional photos or initiate a return through CSSBuy.</p>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                  Stay Safe When Buying
                </h3>
                <p className="text-sm text-foreground-600 mb-6">
                  Explore the CSSBuy Spreadsheet and follow our safety guidelines for a secure buying experience.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-external-link-line" />
                    {t('explore_spreadsheet')}
                  </a>
                  <button
                    onClick={() => navigate('/cssbuy-shipping-guide')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-foreground-700 font-medium rounded-lg border border-background-200 hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-line" />
                    {t('view_shipping_guide')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}